import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface UpcomingWorkloadItem {
  id: string;
  college: string;
  visitDate: string | null;
  candidateCount: number;
  windowLabel: string;
  urgency: 'next_7_days' | 'next_14_days' | 'later';
}

interface UpcomingWorkloadCardProps {
  next7Days: number;
  next14Days: number;
  data: UpcomingWorkloadItem[];
}

const urgencyLabel: Record<UpcomingWorkloadItem['urgency'], string> = {
  next_7_days: 'Next 7 days',
  next_14_days: 'Next 14 days',
  later: 'Later',
};

export function UpcomingWorkloadCard({ next7Days, next14Days, data }: UpcomingWorkloadCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Upcoming Workload</CardTitle>
        <CardDescription>
          Near-term college drives and expected candidate volume
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="text-sm text-muted-foreground">Drives in next 7 days</div>
            <div className="mt-1 text-2xl font-semibold">{next7Days}</div>
          </div>
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="text-sm text-muted-foreground">Drives in next 14 days</div>
            <div className="mt-1 text-2xl font-semibold">{next14Days}</div>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>College</TableHead>
              <TableHead>Window</TableHead>
              <TableHead className="text-right">Candidates</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  No upcoming workload in the selected year
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="font-medium">{item.college}</div>
                    <div className="text-xs text-muted-foreground">{item.visitDate || 'Visit date not set'}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.urgency === 'next_7_days' ? 'default' : 'outline'}>
                      {urgencyLabel[item.urgency]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary">{item.candidateCount}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
